import React, { useState } from 'react';
import { api } from '../services/api';
import { base64ToBlob, saveBlob } from '../lib/downloadFile';
import { TimeRange } from '../types/superadmin';
import { rangeLabel } from './RangeControl';

const BUTTON_LABEL = 'Download usage report (.xlsx)';
const BUTTON_LABEL_BUSY = 'Preparing report…';
const FALLBACK_ERROR = 'The usage report could not be generated. Please try again.';

interface UsageReportButtonProps {
  range: TimeRange;
}

/**
 * Downloads the production clients' usage summary for the selected range. The
 * report always covers the production tier, whichever tier the portal shows.
 */
export default function UsageReportButton({ range }: UsageReportButtonProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClick = async () => {
    setIsBusy(true);
    setErrorMessage(null);
    try {
      const response = await api.getUsageReport(range);
      const file = response?.data;
      if (!file?.contentBase64) throw new Error(FALLBACK_ERROR);
      saveBlob(base64ToBlob(file.contentBase64, file.contentType), file.fileName);
    } catch (error) {
      setErrorMessage(error instanceof Error && error.message ? error.message : FALLBACK_ERROR);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
      <button
        type="button"
        className="btn"
        onClick={handleClick}
        disabled={isBusy}
        title={`Production clients, ${rangeLabel(range)}`}
      >
        {isBusy ? BUTTON_LABEL_BUSY : BUTTON_LABEL}
      </button>
      {errorMessage && (
        <span role="alert" style={{ color: 'var(--error)', fontSize: 12 }}>
          {errorMessage}
        </span>
      )}
    </div>
  );
}
